import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Play, Pause, SkipBack, SkipForward, X, Volume2, VolumeX, GraduationCap } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { tr } from "@/lib/i18n";

const VOICE_OK = typeof window !== "undefined" && "speechSynthesis" in window;

export default function TourPlayer({ steps = [], title = "Guided tour", open, onClose }) {
    const { lang } = useLanguage();
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [muted, setMuted] = useState(false);
    const [rect, setRect] = useState(null);
    const timer = useRef(null);
    const audioRef = useRef(null);
    const audioCache = useRef(new Map()); // step text -> Blob (null = fetch in flight)

    const stepText = (s) => (lang === "fr" ? (s.voiceFr || s.bodyFr || s.voice || s.body) : (s.voice || s.body));

    const fetchTts = (text) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tts`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
    }).then((r) => (r.ok ? r.blob() : Promise.reject(new Error("tts failed"))));

    // Warm every step's narration the moment the tour opens so playback starts instantly
    useEffect(() => {
        if (!open) return;
        steps.forEach((s) => {
            const text = stepText(s);
            if (!text || audioCache.current.has(text)) return;
            audioCache.current.set(text, null);
            fetchTts(text)
                .then((b) => audioCache.current.set(text, b))
                .catch(() => audioCache.current.delete(text));
        });
        // eslint-disable-next-line
    }, [open]);
    const stopAudio = () => {
        if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
        if (VOICE_OK) window.speechSynthesis.cancel();
    };

    const step = steps[idx];

    useEffect(() => {
        if (open) {
            setIdx(0);
            setPlaying(true);
        } else {
            stopAudio();
        }
        // eslint-disable-next-line
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setRect(null);
        if (!step?.target) return;
        const el = document.querySelector(`[data-testid="${step.target}"]`);
        if (!el) return;
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        const t = setTimeout(() => setRect(el.getBoundingClientRect()), 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [open, idx]);

    useEffect(() => {
        if (!open || !step) return;
        clearTimeout(timer.current);
        stopAudio();
        if (!playing) return;
        const text = lang === "fr" ? (step.voiceFr || step.bodyFr || step.voice || step.body) : (step.voice || step.body);
        let done = false;
        let cancelled = false;
        const advance = () => {
            if (done || cancelled) return;
            done = true;
            if (idx < steps.length - 1) setIdx((v) => v + 1);
            else setPlaying(false);
        };
        const speakFallback = () => {
            if (cancelled || !VOICE_OK) { timer.current = setTimeout(advance, 6000); return; }
            const u = new SpeechSynthesisUtterance(text);
            u.rate = 1.03;
            u.onend = () => setTimeout(advance, 400);
            window.speechSynthesis.speak(u);
            timer.current = setTimeout(advance, Math.max(7000, text.length * 110));
        };
        if (!muted) {
            (async () => {
                try {
                    let blob = audioCache.current.get(text);
                    if (blob === null) {
                        blob = await new Promise((resolve) => {
                            const t0 = Date.now();
                            const iv = setInterval(() => {
                                const v = audioCache.current.get(text);
                                if (v || v === undefined || Date.now() - t0 > 20000) { clearInterval(iv); resolve(v || null); }
                            }, 250);
                        });
                    }
                    if (!blob) {
                        blob = await fetchTts(text);
                        audioCache.current.set(text, blob);
                    }
                    if (cancelled) return;
                    const url = URL.createObjectURL(blob);
                    const audio = new Audio(url);
                    audioRef.current = audio;
                    audio.onended = () => { URL.revokeObjectURL(url); setTimeout(advance, 450); };
                    audio.onerror = () => { URL.revokeObjectURL(url); speakFallback(); };
                    try {
                        await audio.play();
                    } catch {
                        // Autoplay blocked — wait for the user's next click instead of dropping to the robot voice
                        await new Promise((resolve) => {
                            const h = () => resolve();
                            document.addEventListener("pointerdown", h, { once: true });
                            setTimeout(resolve, 10000);
                        });
                        if (cancelled) return;
                        await audio.play().catch(() => {});
                    }
                    timer.current = setTimeout(advance, Math.max(9000, text.length * 130));
                } catch {
                    speakFallback();
                }
            })();
        } else {
            timer.current = setTimeout(advance, 6000);
        }
        return () => { cancelled = true; clearTimeout(timer.current); stopAudio(); };
        // eslint-disable-next-line
    }, [open, idx, playing, muted]);

    useEffect(() => () => { stopAudio(); }, []); // eslint-disable-line

    const close = () => { stopAudio(); onClose(); };

    if (!open || !step) return null;

    const vw = window.innerWidth, vh = window.innerHeight;
    const CARD_W = 380;
    let cardStyle = { left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: CARD_W };
    if (rect) {
        const below = rect.bottom + 16 + 280 < vh;
        const left = Math.min(Math.max(rect.left + rect.width / 2 - CARD_W / 2, 16), vw - CARD_W - 16);
        cardStyle = { left, top: below ? rect.bottom + 16 : Math.max(16, rect.top - 16 - 280), width: CARD_W };
    }

    const last = idx === steps.length - 1;

    return createPortal(
        <div className="fixed inset-0 z-[100]" data-testid="tour-overlay">
            {rect ? (
                <>
                    {/* Four dim panels — the highlighted element stays fully clear */}
                    <div className="absolute bg-[#04101e]/45" style={{ left: 0, top: 0, right: 0, height: Math.max(0, rect.top - 10) }} onClick={close} />
                    <div className="absolute bg-[#04101e]/45" style={{ left: 0, top: rect.bottom + 10, right: 0, bottom: 0 }} onClick={close} />
                    <div className="absolute bg-[#04101e]/45" style={{ left: 0, top: rect.top - 10, width: Math.max(0, rect.left - 10), height: rect.height + 20 }} onClick={close} />
                    <div className="absolute bg-[#04101e]/45" style={{ left: rect.right + 10, top: rect.top - 10, right: 0, height: rect.height + 20 }} onClick={close} />
                    <div data-testid="tour-spotlight" className="absolute rounded-[14px] border-2 border-[#00B4D8] shadow-[0_0_0_6px_rgba(0,180,216,0.3),0_0_40px_rgba(0,180,216,0.35)] pointer-events-none transition-all duration-500"
                        style={{ left: rect.left - 8, top: rect.top - 8, width: rect.width + 16, height: rect.height + 16 }} />
                </>
            ) : (
                <div className="absolute inset-0 bg-[#04101e]/45" onClick={close} />
            )}
            <div className="absolute" style={cardStyle}>
                <div className="bg-[#0A2540]/95 backdrop-blur-xl text-white rounded-[18px] border border-white/15 shadow-2xl p-6" data-testid="tour-card">
                    <div className="flex items-center justify-between mb-3">
                        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#7CD4EA]">
                            <GraduationCap className="w-3.5 h-3.5" /> {tr(title)}
                        </span>
                        <button data-testid="tour-close-btn" onClick={close} className="p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="font-heading font-bold text-lg" data-testid="tour-step-title">{lang === "fr" ? (step.titleFr || step.title) : step.title}</div>
                    <p className="text-sm text-slate-300 leading-relaxed mt-2" data-testid="tour-step-body">{lang === "fr" ? (step.bodyFr || step.body) : step.body}</p>
                    <div className="mt-4 h-1 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-[#00B4D8] rounded-full transition-all duration-500" style={{ width: `${((idx + 1) / steps.length) * 100}%` }} data-testid="tour-progress" />
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                        <span className="text-[11px] font-data text-slate-400">{idx + 1} / {steps.length}</span>
                        <div className="flex items-center gap-1.5">
                            <button data-testid="tour-prev-btn" onClick={() => setIdx((v) => Math.max(0, v - 1))} disabled={idx === 0} className="p-2 rounded-[10px] text-white/60 hover:bg-white/10 disabled:opacity-30 transition-colors"><SkipBack className="w-4 h-4" /></button>
                            <button data-testid="tour-play-btn" onClick={() => setPlaying((p) => !p)} className="p-2.5 rounded-[10px] bg-[#00B4D8] hover:bg-[#00c3eb] text-white transition-colors">
                                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            </button>
                            {last ? (
                                <button data-testid="tour-finish-btn" onClick={close} className="px-4 py-2 rounded-[10px] bg-[#06D6A0] text-white text-xs font-bold hover:opacity-90 transition-opacity">{tr("Finish")}</button>
                            ) : (
                                <button data-testid="tour-next-btn" onClick={() => setIdx((v) => Math.min(steps.length - 1, v + 1))} className="p-2 rounded-[10px] text-white/60 hover:bg-white/10 transition-colors"><SkipForward className="w-4 h-4" /></button>
                            )}
                            <button data-testid="tour-mute-btn" onClick={() => setMuted((m) => !m)} title={muted ? tr("Unmute narration") : tr("Mute narration")} className="p-2 rounded-[10px] text-white/60 hover:bg-white/10 transition-colors">
                                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    {!VOICE_OK && <div className="mt-3 text-[10px] text-slate-400">{tr("Voice narration isn't supported in this browser — the tour auto-advances instead.")}</div>}
                </div>
            </div>
        </div>,
        document.body
    );
}
